/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { CustomTextDocumentMetadata } from 'src/customText/core/stores/CustomTextDocumentStore';

/**
 * Fetches the document's content and
 * returns a document store complete in data and its loading states.
 *
 * @param param0.documentName name of the document.
 */
export const useFetchDocument = (documentName: string) => {
    const { dispatcher, rootStore } = useGlobalState();
    const documentMetadata: CustomTextDocumentMetadata = React.useMemo(
        () => rootStore.currentCustomTextProjectStore.documents.find(d => d.name === documentName),
        [documentName, rootStore.currentCustomTextProjectStore.documents]
    );
    const documentStore = rootStore.currentCustomTextProjectStore.currentDocumentStore;
    const savingCurrentDocumentTracker = useProgressTracker({
        onComplete: () => {
            dispatcher.dispatch('switchCurrentDocumentStore', { newDocumentName: documentMetadata.name });
        }
    });

    React.useEffect(() => () => dispatcher.dispatch('closeCurrentDocumentStore'), []);

    React.useEffect(() => {
        if (documentMetadata?.name && documentMetadata.name !== documentStore?.name) {
            dispatcher.dispatch('saveDocumentStoresChanges', { tracker: savingCurrentDocumentTracker });
        }
    }, [documentMetadata?.name]);

    const fetchDocumentTracker = useProgressTracker();

    React.useEffect(() => {
        /**
         * Ensures that the document store is not fetched.
         */
        if (documentStore?.isInitialized) {
            return;
        }

        /**
         * Fetches document text.
         */
        if (documentStore) {
            dispatcher.dispatch('fetchDocumentContentByName', {
                documentName: documentStore.name,
                localStore: documentStore,
                tracker: fetchDocumentTracker,
                toastData: {}
            });
        }
    }, [documentStore]);

    return {
        documentStore,
        isLoading: !documentStore?.isInitialized || fetchDocumentTracker.isRunning,
        isFailed: fetchDocumentTracker.isFailed
    };
};
